<template>
    <Listbox
        v-slot="{ open }"
        v-model="modelValue"
        :disabled="disabled"
    >
        <div :class="['select-wrapper', customClass]">
            <ListboxButton :class="['select-trigger', { 'select-trigger-disabled': disabled }]">
                <span class="select-value">
                    {{ selectedLabel || placeholder }}
                </span>
                <span :class="['select-arrow', { 'is-open': open }]">
                    <svg
                        width="20"
                        height="20"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                    >
                        <path
                            fill-rule="evenodd"
                            d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z"
                            clip-rule="evenodd"
                        />
                    </svg>
                </span>
            </ListboxButton>

            <Transition
                enter-active-class="select-enter-active"
                enter-from-class="select-enter-from"
                enter-to-class="select-enter-to"
                leave-active-class="select-leave-active"
                leave-from-class="select-leave-from"
                leave-to-class="select-leave-to"
            >
                <ListboxOptions class="select-options">
                    <ListboxOption
                        v-for="option in options"
                        :key="option.value"
                        v-slot="{ active, selected }"
                        :value="option.value"
                        :disabled="option.disabled"
                        as="template"
                    >
                        <li
                            :class="[
                                'select-option',
                                { 'select-option-active': active, 'select-option-disabled': option.disabled },
                            ]"
                        >
                            <span :class="['select-option-label', { 'is-selected': selected }]">
                                {{ option.label }}
                            </span>
                            <span
                                v-if="selected"
                                class="select-check-icon"
                            >
                                <svg
                                    width="20"
                                    height="20"
                                    viewBox="0 0 20 20"
                                    fill="currentColor"
                                >
                                    <path
                                        fill-rule="evenodd"
                                        d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z"
                                        clip-rule="evenodd"
                                    />
                                </svg>
                            </span>
                        </li>
                    </ListboxOption>
                </ListboxOptions>
            </Transition>
        </div>
    </Listbox>
</template>

<script setup lang="ts">
    import { computed } from 'vue'
    import { Listbox, ListboxButton, ListboxOptions, ListboxOption } from '@headlessui/vue'

    defineOptions({ name: 's-select' })

    interface Option {
        label: string
        value: string | number
        disabled?: boolean
    }

    interface Props {
        options: Option[]
        placeholder?: string
        disabled?: boolean
        customClass?: string
    }

    const props = withDefaults(defineProps<Props>(), {
        placeholder: '请选择',
        disabled: false,
        customClass: '',
    })

    const modelValue = defineModel<string | number>()

    const selectedLabel = computed(() => {
        const option = props.options.find(o => o.value === modelValue.value)
        return option?.label
    })
</script>

<style scoped lang="scss">
    .select-wrapper {
        position: relative;
        width: 100%;
    }

    .select-trigger {
        position: relative;
        display: flex;
        align-items: center;
        box-sizing: border-box;
        width: 100%;
        padding: 0.5rem 2.5rem 0.5rem 0.75rem;
        border: 1px solid var(--color-gray-300, #d1d5db);
        border-radius: 0.5rem;
        font-size: 0.875rem;
        text-align: left;
        background-color: var(--color-bg-white, #fff);
        transition: all 0.15s ease;
        cursor: pointer;

        &:focus {
            border-color: var(--color-primary, #f97316);
            outline: none;
            box-shadow: 0 0 0 3px var(--color-primary-200, rgb(249 115 22 / 20%));
        }

        &.select-trigger-disabled {
            color: var(--color-gray-500, #6b7280);
            background-color: var(--color-gray-50, #f9fafb);
            cursor: not-allowed;
        }
    }

    .select-value {
        display: block;
        text-overflow: ellipsis;
        white-space: nowrap;
        overflow: hidden;
    }

    .select-arrow {
        position: absolute;
        top: 0;
        right: 0;
        bottom: 0;
        display: flex;
        align-items: center;
        padding-right: 0.5rem;
        color: var(--color-gray-400, #9ca3af);
        pointer-events: none;

        svg {
            transition: transform 0.2s ease;
        }

        &.is-open svg {
            transform: rotate(180deg);
        }
    }

    .select-options {
        position: absolute;
        z-index: 50;
        width: 100%;
        max-height: 15rem;
        padding: 0.25rem 0;
        margin-top: 0.25rem;
        border-radius: 0.5rem;
        outline: 1px solid rgb(0 0 0 / 5%);
        font-size: 0.875rem;
        background-color: var(--color-bg-white, #fff);
        box-shadow:
            0 10px 15px -3px rgb(0 0 0 / 10%),
            0 4px 6px -2px rgb(0 0 0 / 5%);
        overflow: auto;

        &:focus {
            outline: none;
        }
    }

    .select-option {
        position: relative;
        display: flex;
        align-items: center;
        padding: 0.5rem 1rem 0.5rem 2.5rem;
        color: var(--color-gray-900, #111827);
        cursor: pointer;
        user-select: none;

        &.select-option-active {
            color: var(--color-primary-900, #7c2d12);
            background-color: var(--color-primary-50, #fff7ed);
        }

        &.select-option-disabled {
            opacity: 0.5;
            cursor: not-allowed;
        }
    }

    .select-option-label {
        display: block;
        font-weight: 400;
        text-overflow: ellipsis;
        white-space: nowrap;
        overflow: hidden;

        &.is-selected {
            font-weight: 500;
        }
    }

    .select-check-icon {
        position: absolute;
        top: 0;
        bottom: 0;
        left: 0;
        display: flex;
        align-items: center;
        padding-left: 0.75rem;
        color: var(--color-primary, #f97316);
    }

    // 过渡动画 - 进入
    .select-enter-active {
        transition:
            opacity 0.15s ease-out,
            transform 0.15s ease-out;
    }

    .select-enter-from {
        opacity: 0;
        transform: translateY(-4px);
    }

    .select-enter-to {
        opacity: 1;
        transform: translateY(0);
    }

    // 过渡动画 - 离开
    .select-leave-active {
        transition:
            opacity 0.1s ease-in,
            transform 0.1s ease-in;
    }

    .select-leave-from {
        opacity: 1;
        transform: translateY(0);
    }

    .select-leave-to {
        opacity: 0;
        transform: translateY(-4px);
    }
</style>
